import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'

import { useCreatePointOfSale } from '@/api/api-mutations'
import { useListSalesChannels } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { FocusModal } from '@/components/base/focus-modal'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/settings/point-of-sale/create')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.Settings.PointOfSale.CreateTitle()
	}
})

function RouteComponent() {
	return (
		<RouteModal>
			<NewPointOfSaleForm />
		</RouteModal>
	)
}

function NewPointOfSaleForm() {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()

	const salesChannels = useListSalesChannels()
	const createPointOfSale = useCreatePointOfSale()

	const salesChannelOptions = useMemo(() => {
		return (
			salesChannels.data?.map(channel => ({
				value: channel.uuid,
				label: channel.name
			})) || []
		)
	}, [salesChannels.data])

	const form = useAppForm({
		defaultValues: {
			name: '',
			identifier: '',
			salesChannelUuids: [] as string[]
		},
		onSubmit: async ({ value }) => {
			const result = await createPointOfSale({
				body: {
					identifier: value.identifier,
					name: value.name,
					salesChannelUuids: value.salesChannelUuids
				}
			})
			if (result.isSuccess) {
				toast.success(L.Settings.PointOfSale.CreateSuccess())
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<FocusModal.Header>
				<FocusModal.Title asChild>
					<Heading>{L.Settings.PointOfSale.CreateTitle()}</Heading>
				</FocusModal.Title>
			</FocusModal.Header>
			<FocusModal.Body className='flex-1 flex-col items-center justify-center overflow-auto px-6 py-4'>
				<FormContainer className='max-w-[500px] justify-self-center'>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => (
								<field.TextField
									autoFocus
									label={L.Fields.Name()}
									placeholder={L.Settings.PointOfSale.Create.NamePlaceholder()}
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='identifier'
							children={field => (
								<field.TextField
									label={L.Settings.PointOfSale.Column.Identifier()}
									placeholder={L.Settings.PointOfSale.Create.IdentifierPlaceholder()}
									optional
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='salesChannelUuids'
							children={field => (
								<field.ComboboxField
									label={L.Fields.SalesChannel()}
									placeholder={L.Settings.PointOfSale.Create.SelectSalesChannels()}
									multiple
									optional
									options={salesChannelOptions}
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</FocusModal.Body>
			<FocusModal.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<FocusModal.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</FocusModal.Close>
					<form.CreateButton />
				</div>
			</FocusModal.Footer>
		</form.AppForm>
	)
}
